import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EvaluationQueryViewStore } from "../../store";
import { EvaluationQueryView } from "../view/EvaluationQueryView";
import { EvaluationQueryUpdateStore } from "../../store";
import { EvaluationQueryUpdate } from "../update/EvaluationQueryUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EvaluationQueryModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EvaluationQueryModalContent: React.FC<EvaluationQueryModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EvaluationQueryUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EvaluationQueryViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EvaluationQueryView
            evaluationQueryViewStore={
              storeInfo.store as EvaluationQueryViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <EvaluationQueryUpdate
            evaluationQueryUpdateStore={
              storeInfo.store as EvaluationQueryUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
